"use client"

/*
 * DEV HANDOFF - feed-settings.tsx
 *
 * Automation (feed) settings. Seven tabbed sections carrying the legacy field
 * inventory of `Your Feeds > [feed] > Settings`.
 *
 * Three files, one screen:
 *   settings-page.tsx   the shell (header, tabs, footer, delete card + dialog)
 *   settings-form.tsx   the divided settings list and its field union
 *   feed-settings.data.ts  the field spec - MOCK DATA, does not ship
 *
 * `social-settings.tsx` wears the same shell. Keep them that way: a change to
 * the header, the footer or the delete treatment belongs in settings-page.tsx,
 * not here.
 *
 * WIRING (production owns all of it):
 * - `sections` is the whole data contract. Build it from the input record the
 *   way `ui/src/lib/social-input-configure.ts` does, carrying each field's
 *   legacy `data-field` dot path in `SettingsField.name` (already populated).
 * - Every control is inert. Wire changes through `FormUtilityMixin.requestChange`.
 * - `dirty` / `saving` / `errorCounts` / `formError` come from `InputEdit.jsx`'s
 *   existing deep-equal diff and `ErrorModule` plumbing.
 *
 * NOT BUILT THIS ROUND, rendered as `static` placeholders and flagged:
 * - Update window (repeating schedule rows, legacy `ScheduleComponent`)
 * - Default location (Google Maps picker, legacy `InputLocationPanel`)
 *
 * COPY: labels are sentence-cased per the locked copy rules. The full
 * before/after list is in HANDOFF.md. The screen noun is "Automation settings",
 * matching the Figma frame and the locked vocabulary.
 */

import * as React from "react"
import { History, Stethoscope } from "lucide-react"

import {
  sourceLabel,
  sourceNetwork,
  type FeedSubtype,
  type SourceType,
} from "@/shadcn/components/settings/settings-vocab"
import { type SettingsSection } from "@/shadcn/components/settings/settings-form"
import {
  SettingsDeleteDialog,
  SettingsPage,
  type SettingsPageProps,
  SettingsAvatar,
} from "@/shadcn/components/settings/settings-page"

type FeedSettingsProps = Partial<SettingsPageProps> & {
  /**
   * REQUIRED HERE, unlike the preview kit, where it defaults to a mock
   * fixture. Prod builds this from the stored record; there is no fixture to
   * fall back to. See `settings-vocab.ts` for why the split exists.
   */
  sections: SettingsSection[]
  /**
   * Drives the default meta line. The SECTIONS come from `sections`, which the
   * caller builds with the same source - see `buildFeedSections`, which prunes
   * the tab set and swaps the General tab's first slot per source type.
   */
  source?: SourceType
  /** RSS flavour (Pinterest, Etsy, Shopify, ...). Label only. */
  subtype?: FeedSubtype
  /** The automation's name, for the meta line. */
  sourceName?: string
  /**
   * The picture the legacy avatar shows: `input.icon` for a source,
   * `social.icon` (else `social.src`) for a destination. Omitted, or failing
   * to load, the avatar falls back to initials.
   */
  avatarUrl?: string
}

export default function FeedSettings({
  sections,
  source = "rss",
  subtype,
  sourceName = "TechCrunch",
  avatarUrl,
  title,
  meta,
  backLabel = "Automations",
  note = "These settings apply to every post from this automation.",
  actions = [
    { label: "History", icon: <History /> },
    { label: "Medic", icon: <Stethoscope /> },
  ],
  ...props
}: FeedSettingsProps) {
  return (
    <SettingsPage
      sections={sections}
      /*
       * The name leads and the settings noun sits under it (Emily,
       * 2026-10-01): the reader arrives knowing which automation they opened, so
       * that is what the big line confirms. Same type as before, swapped.
       */
      title={title ?? sourceName}
      icon={
        <SettingsAvatar
          name={sourceName}
          avatarUrl={avatarUrl}
          network={sourceNetwork(source, subtype)}
        />
      }
      meta={meta ?? `Automation settings · ${sourceLabel(source, subtype)}`}
      backLabel={backLabel}
      note={note}
      actions={actions}
      {...props}
      /* No delete card here (Emily, 2026-10-01): deleting lives on the list
         this screen was opened from. Set after the spread so a caller cannot
         turn it back on. Complex keeps its own on Source settings. */
      showDelete={false}
    />
  )
}

/**
 * "Delete this automation?" - Figma `29890:1501`. A thin wrapper over the
 * shared `SettingsDeleteDialog` so the copy lives next to the screen it belongs
 * to. Surface only; drop it into `AlertDialogContent` in prod with the
 * `.tw-root` portal patch.
 */
export function DeleteAutomationDialog({
  automationName = "TechCrunch",
  ...props
}: Omit<
  React.ComponentProps<typeof SettingsDeleteDialog>,
  "title" | "description" | "confirmLabel"
> & { automationName?: string }) {
  return (
    <SettingsDeleteDialog
      title="Delete this automation?"
      /* Figma says "stay on your socials"; the locked vocabulary says
         destinations. Changed, and flagged in HANDOFF.md. */
      description={`We will stop checking ${automationName} and remove it from your automations. Posts we already published stay on your destinations. This cannot be undone.`}
      confirmLabel="Delete automation"
      {...props}
    />
  )
}
